/**
 * The app's screen header.
 *
 * Every stack screen that isn't a tab used to hand-roll this: the same row, the
 * same `paddingTop: 56`, the same `headerBtn`, copied seventeen times. That is
 * why the notch bug and the undersized tap targets had to be fixed seventeen
 * times too. This is the one place that shape lives now.
 *
 * The layout is deliberately not react-navigation's header — these screens are
 * presented with `headerShown: false` and draw their own chrome — but it stays
 * inside the top safe-area inset via `theme.headerPaddingTop`.
 */

import React from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useTheme } from '../theme/ThemeContext';
import { useThemedStyles } from '../theme/useThemedStyles';
import Icon, { ICON_SIZES } from './Icon';
import type { IconName } from './Icon';
import type { Theme } from '../theme/types';

/** How a text action is weighted. Icons use `tone` only to pick a colour. */
export type ActionTone =
  /** Dismissive, e.g. "Cancel". */
  | 'muted'
  /** The screen's confirming action, e.g. "Save", "Send". */
  | 'primary'
  /** A secondary additive action, e.g. "+ New". */
  | 'accent'
  /** Destructive, e.g. the delete icon. */
  | 'danger';

export interface HeaderAction {
  /** Spoken by VoiceOver. Required — it is often more specific than the
   *  visible text ("Save activity" for a button that reads "Save"). */
  label: string;
  onPress: () => void;
  testID: string;
  /** Render this icon instead of text. */
  icon?: IconName;
  /** Visible text. Ignored when `icon` is set; defaults to `label`. */
  text?: string;
  tone?: ActionTone;
  disabled?: boolean;
  /** Swaps the content for a spinner and blocks presses. */
  loading?: boolean;
  accessibilityHint?: string;
}

interface ScreenHeaderProps {
  title: string;
  /** Truncate the title rather than let it wrap the header taller. */
  titleNumberOfLines?: number;
  left?: HeaderAction;
  /** One action, several, or none. When absent a spacer keeps the title
   *  centred against the left action. */
  right?: HeaderAction | HeaderAction[];
}

export default function ScreenHeader({
  title,
  titleNumberOfLines = 1,
  left,
  right,
}: ScreenHeaderProps) {
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const rightActions = right ? (Array.isArray(right) ? right : [right]) : [];

  const renderAction = (action: HeaderAction, key: string) => {
    const isBlocked = Boolean(action.disabled || action.loading);
    return (
      <TouchableOpacity
        key={key}
        style={styles.button}
        onPress={action.onPress}
        disabled={isBlocked}
        accessibilityRole="button"
        accessibilityLabel={action.label}
        accessibilityHint={action.accessibilityHint}
        accessibilityState={{ disabled: isBlocked }}
        testID={action.testID}
      >
        {action.loading ? (
          <ActivityIndicator size="small" color={colors.primary} accessible={false} />
        ) : action.icon ? (
          <Icon
            name={action.icon}
            size={ICON_SIZES.header}
            color={action.tone === 'danger' ? colors.error : colors.textSecondary}
          />
        ) : (
          <Text
            style={[
              styles.label,
              action.tone === 'primary' && styles.labelPrimary,
              action.tone === 'accent' && styles.labelAccent,
              action.tone === 'danger' && styles.labelDanger,
              action.disabled && styles.labelDisabled,
            ]}
            accessible={false}
          >
            {action.text ?? action.label}
          </Text>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.header}>
      <View style={styles.side}>{left && renderAction(left, 'left')}</View>

      <Text
        style={styles.title}
        numberOfLines={titleNumberOfLines}
        accessibilityRole="header"
      >
        {title}
      </Text>

      <View style={[styles.side, styles.sideRight]}>
        {rightActions.map((action, i) => renderAction(action, action.testID || `right-${i}`))}
      </View>
    </View>
  );
}

const createStyles = (theme: Theme) => ({
  header: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    paddingHorizontal: theme.spacing.lg,
    // Derived from the live top inset, so the row clears the status bar, the
    // notch and the Dynamic Island on every device.
    paddingTop: theme.headerPaddingTop,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.background,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  /** Holds the edge actions. The minimum width is what keeps the title optically
   *  centred on screens whose right side is empty. */
  side: {
    minWidth: 44,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
  },
  sideRight: {
    justifyContent: 'flex-end' as const,
  },
  title: {
    flex: 1,
    fontSize: theme.fs(18),
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    textAlign: 'center' as const,
  },
  /** 44x44 is the minimum comfortable touch target in the HIG. */
  button: {
    padding: theme.spacing.sm,
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  label: {
    fontSize: theme.fs(16),
    color: theme.colors.textMuted,
    fontWeight: '500' as const,
  },
  labelPrimary: {
    color: theme.colors.primary,
    fontWeight: '700' as const,
  },
  labelAccent: {
    fontSize: theme.fs(15),
    color: theme.colors.primary,
    fontWeight: '600' as const,
  },
  labelDanger: {
    color: theme.colors.error,
    fontWeight: '600' as const,
  },
  labelDisabled: {
    color: theme.colors.borderMedium,
  },
});
